<template>
  <div :class="wrapperClasses">
    <div class="listing">
      <b-field class="search-controls">
        <b-select v-if="primaryFilterOptions.length > 0" placeholder="Status filter" v-model="primaryFilter">
          <option v-for="opt in primaryFilterOptions" :value="opt.key" :key="opt.key">{{ opt.label }}</option>
        </b-select>
        <b-select v-if="mediaFilterOptions.length > 0" placeholder="Media filter" v-model="mediaFilter">
          <option v-for="opt in mediaFilterOptions" :value="opt.key" :key="opt.key">{{ opt.label }}</option>
        </b-select>
        <b-select placeholder="By surname" v-model="lastName">
          <option v-for="opt in letters" :value="opt.key" :key="['letter', opt.key].join('-')">{{ opt.label }}</option>
        </b-select>
        <b-select placeholder="Sort" v-model="sortOpt">
          <option v-for="opt in sortOptions" :value="opt.key" :key="['sort', opt.key].join('-')">{{ opt.label }}
          </option>
        </b-select>
        <div class="total" @click="reload" title="Click to reload">{{ totalLabel }}</div>
        <span class="search-wrapper">
          <input v-model="search" size="60" placeholder="Search" v-on:keyup.enter="applyFilter" class="search" />
          <b-icon icon="close" @click.native="clearSearch" />
        </span>
        <span v-on:click="applyFilter" class="button-wrapper">
          <b-radio-button v-model="searchMode" native-value="usearch" type="is-light">
            <b-icon icon="magnify" />
          </b-radio-button>
        </span>
        <b-button type="is-success" class="align-right" @click="updateStatuses"
          title="Update edited status values">Update</b-button>
      </b-field>
      <b-table :data="items" :paginated="true" backend-pagination :current-page="page" :per-page="perPage"
        :total="total" @page-change="onPageChange" :row-class="(row, index) => assignRowClasses(row, index)">
        <template slot-scope="props">
          <b-table-column field="uid" label="ID" class="uid" sortable>{{ props.row.uid }}</b-table-column>
          <b-table-column field="fullName" label="Applicant" class="name-column" sortable>{{ props.row.fullName
            }}</b-table-column>
          <b-table-column field="email" label="Email" class="email-column" sortable>
            <a :href="props.row.email | mailto" :title="props.row.email | mailTooltip">{{ props.row.email }}</a>
            <span :id="['t-row',props.row._id].join('-')" class="scroll-to-anchor"></span>
          </b-table-column>
          <b-table-column field="works" label="Works">
            <ol v-if="props.row.numWorks > 0" class="works-list">
              <li v-for="(work, wi) in props.row.works" :key="['subwork', work._id, wi].join('-')">
                <span class="text" :title="work.medium">{{ work.title }}</span>
              </li>
            </ol>
          </b-table-column>
          <b-table-column field="numMediaItems" class="num-row" label="# media items">
            <span v-on:click="showSubmission(props.row._id)" class="horizontal-icons">
              {{ props.row.numMediaItems }}
              <b-icon v-if="props.row.hasVideo" icon="video-vintage" :title="props.row.numVideos" />
              <b-icon v-if="props.row.hasImages" icon="image-area" :title="props.row.numImages" />
              <b-icon v-if="props.row.hasAudio" icon="volume-high" :title="props.row.numAudio" />
            </span>
          </b-table-column>
          <b-table-column field="numMediaItems" class="date-column" label="Updated">
            <b-tooltip class="created-at" :label="updateHint(props.row)" position="is-left">{{ props.row.modifiedAt |
              shortDate }}</b-tooltip>
          </b-table-column>
          <b-table-column field="Progress" class="check-column" label="Progress" sortable>
            <progress-row :submission="props.row" />
          </b-table-column>
          <b-table-column field="status" class="status-column" label="Status">
            <b-checkbox v-model="props.row.ineligible" @change.native="handleStatus(props.row, 'ineligible')"
              size="is-small" class="warning-color">ineligible</b-checkbox>
            <b-checkbox v-model="props.row.marked" @change.native="handleStatus(props.row, 'marked')" size="is-small"
              class="blue">queried</b-checkbox>
            <b-checkbox v-model="props.row.checked" @change.native="handleStatus(props.row, 'checked')" size="is-small"
              class="green">checked</b-checkbox>
            <b-checkbox v-model="props.row.deadlineException"
              @change.native="handleStatus(props.row, 'deadlineException')" size="is-small"
              class="active-color">deadline extension</b-checkbox>
          </b-table-column>
          <b-table-column field="edit" label="Edit" class="edit-column">
            <span class="edit" v-on:click="showSubmission(props.row._id)">
              <b-icon icon="square-edit-outline" />
            </span>
          </b-table-column>
        </template>
      </b-table>
      <b-button type="is-success" class="align-right" @click="updateStatuses"
        title="Update edited status values">Update</b-button>
    </div>
    <b-loading :is-full-page="true" :active.sync="loading" :can-cancel="true"></b-loading>
    <SubmissionDetail v-if="showDetail" :submission="submission" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  listSubmissions,
  bulkEdit,
  removeMediaItem,
  removeWork,
  saveSubmission
} from "../api/methods";
import filters from "../mixins/filters";
import { submissionSchema } from "../store/submission";
import {
  mapSubmission,
  mapWork,
  mapMediaItem,
  disableSubmissionStatuses,
  extractSubAndWorkRefs
} from "../api/mappers";
import { notEmptyString } from "../api/helpers";
import SubmissionDetail from "./SubmissionDetail";
import ProgressRow from "./ProgressRow";
import { bus } from "../main";

export default {
  name: "SubmissionList",
  props: {
    msg: String
  },
  components: {
    SubmissionDetail,
    ProgressRow
  },
  mixins: [filters],
  data() {
    return {
      items: [],
      num: 0,
      total: 0,
      page: 1,
      perPage: 100,
      criteria: {
        valid: 1
      },
      search: "",
      searchMode: "usearch",
      primaryFilter: "valid",
      mediaFilter: "all",
      primaryFilterOptions: [
        { key: "valid", label: "Eligible", title: "Submitted and eligible" },
        { key: "ineligible", label: "Ineligible", title: "Ineligible" },
        { key: "longRead", label: "Long read", title: "Long read" },
        { key: "submitted", label: "Submitted", title: "All submitted" },
        {
          key: "paid_incomplete",
          label: "Paid, incomplete",
          title: "Paid, not submitted"
        },
        { key: "unpaid", label: "Not paid", title: "Unpaid" },
        { key: "marked", label: "Queried", title: "Queried" },
        { key: "checked", label: "Checked", title: "Checked" },
        { key: "notchecked", label: "Not checked", title: "Not checked" },
        {
          key: "shortlisted",
          label: "Return to",
          title: "Added to pre-selection list"
        },
        {
          key: "selected",
          label: "Shortlisted",
          title: "Has shortlisted works"
        },
        { key: "all", label: "All", title: "All statuses" }
      ],
      mediaFilterOptions: [
        { key: "all", label: "All" },
        { key: "hasVideo", label: "With moving image(s)" },
        { key: "noVideo", label: "Without moving images" },
      ],
      sortOptions: [
        { key: "user-asc", label: "By User ID" },
        { key: "modified-desc", label: "Latest first" },
      ],
      sortOpt: "user-asc",
      lastName: "",
      showDetail: false,
      submission: submissionSchema,
      loaded: false,
      loading: true,
      filtering: false
    };
  },
  computed: {
    ...mapGetters(["user", "settings", "options", "countries"]),
    wrapperClasses() {
      let cls = ["submissions"];
      if (this.showDetail) {
        cls.push("show-detail");
      }
      if (notEmptyString(this.search)) {
        cls.push("search-mode");
      }
      return cls;
    },
    start() {
      return (this.page - 1) * this.perPage;
    },
    calcTotal() {
      return this.items.length;
    },
    totalLabel() {
      let str = this.num;
      if (this.num < this.total) {
        str += " / " + this.total;
      }
      return str;
    },
    letters() {
      const integers = [...Array(27).keys()];
      const letters = integers.map(v => {
        if (v < 1) {
          return { key: "", label: "All surnames" };
        } else {
          const letter = String.fromCharCode(v + 96);
          return { key: letter, label: letter.toUpperCase() + "..." };
        }
      });
      return letters;
    }
  },
  created() {
    const { query } = this.$route;
    if (query instanceof Object) {
      this.primaryFilter = "valid";
      let mf = "all";
      let search = "";
      const queryEntries = Object.entries(query);
      this.restoreFilters();
      if (queryEntries.length > 0) {
        queryEntries.forEach(entry => {
          const [k, v] = entry;
          switch (k) {
            case "status":
              if (notEmptyString(v, 2)) {
                this.criteria = {};
                this.criteria[k] = v;
                this.primaryFilter = v;
              }
              break;
            case "media":
              if (notEmptyString(v, 2)) {
                this.criteria[k] = v;
                mf = v;
              }
              break;
            case "search":
              if (notEmptyString(v)) {
                this.criteria.usearch = v;
                search = v;
              }
              break;
            case "page":
              this.page = parseInt(v);
              break;
            case "lastName":
              if (v.length === 1) {
                this.lastName = v;
                this.criteria.lastName = v;
              }
              break;
            case "sort":
              if (v.length > 2) {
                this.sortOpt = v;
                this.criteria.sortOpt = v;
              }
              break;
          }
        });
      }
      if (this.criteria.hasOwnProperty("all")) {
        this.criteria.valid = 0;
      }
      if (search.length > 0) {
        this.search = search;
        this.parseSearch();
      } else {
        if (queryEntries.length < 1) {
          this.primaryFilter = "valid";
        }
        this.mediaFilter = mf;
      }
    }
    this.loadSubmissions();
    bus.$on("escape", (val) => {
      if (val) {
        const { path } = this.$route;
        const parts = path.substring(1).split("/");
        if (parts[0] === "submissions" && parts.length === 2) {
          this.close();
        }
      }
    });
    bus.$on("refresh", status => {
      if (status) {
        const { path } = this.$route;
        if (path.indexOf("submissions") >= 0) {
          this.loadSubmissions(true);
        }
      }
    });
    bus.$on("update-media-item", ({ subId, workId, item, newWork }) => {
      this.updateItem(subId, workId, item, newWork);
    });
    bus.$on("update-work", ({ subId, workId, work }) => {
      this.updateWork(subId, workId, work);
    });
    bus.$on("delete-media-item", ({ subId, workId, itemId }) => {
      this.deleteWorkItem(subId, workId, itemId);
    });
    bus.$on("update-status", ({ subId, key, boolVal }) => {
      this.syncSubUpdated(subId, key, boolVal);
    });
    bus.$on("slide-update", ({ subId, key, newVal, workId }) => {
      this.syncSubUpdated(subId, key, newVal, workId);
    });
  },
  mounted() {},
  watch: {
    primaryFilter(cv, pv) {
      if (cv !== pv && !this.filtering) {
        this.search = "";
        this.setCriteria(cv);
      }
    },
    mediaFilter(cv, pv) {
      if (cv !== pv && !this.filtering) {
        if (cv !== "all") {
          this.search = "";
        }
        this.setCriteria();
      }
    },
    lastName(cv, pv) {
      if (cv !== pv && !this.filtering) {
        this.setCriteria();
      }
    },
    sortOpt(cv, pv) {
      if (cv !== pv && !this.filtering) {
        this.loadSubmissions();
      }
    }
  },
  methods: {
    loadSubmissions(skipCleanup) {
      this.filtering = true;
      this.loading = true;
      if (notEmptyString(this.sortOpt, 4)) {
        this.criteria.sort = this.sortOpt;
        this.$ls.set("list_sort", this.sortOpt);
      }
      if (notEmptyString(this.primaryFilter, 1)) {
        this.$ls.set("list_filter", this.primaryFilter);
      }
      listSubmissions(this.start, this.perPage, this.criteria).then(data => {
        if (data.items) {
          this.items = data.items.map(mapSubmission);
          this.total = data.total;
          this.num = data.num;
          if (skipCleanup !== true) {
            this.matchSubmission();
            if (this.loaded) {
              this.setQueryString();
            }
          }
          this.loaded = true;
          setTimeout(() => {
            this.filtering = false;
          }, 500);
          this.loading = false;
        }
      });
      setTimeout(() => {
        this.loading = false;
      }, 5000);
    },
    showSubmission(id, skipPath) {
      const submission = this.items.find(sub => sub._id === id);
      // simple check
      if (submission instanceof Object) {
        this.submission = submission;
        this.showDetail = true;
        if (skipPath !== true) {
          const newPath = "/submissions/" + id;
          if (this.$route.path !== newPath) {
            this.$router.push({
              path: newPath,
              query: this.$route.query
            });
          }
        }
        this.$ls.set("currsub", id);
        this.$scrollTo("body", 250);
      }
    },
    parseSearch() {
      const str = this.search.trim();
      let mode = this.searchMode;
      if (/^[^@ ]+@\w+/.test(str)) {
        mode = "email";
      } else if (/^\d{1,6}$/.test(str)) {
        mode = "uid";
      }
      this.criteria = {};
      this.criteria[mode] = str;
      this.primaryFilter = ["-"];
      this.mediaFilter = "all";
      this.page = 0;
    },
    applyFilter() {
      if (this.search.length > 1) {
        setTimeout(() => {
          this.parseSearch();
          this.loadSubmissions();
        }, 250);
      } else {
        let nc = {};
        Object.entries(this.criteria).forEach(entry => {
          const [k, v] = entry;
          switch (k) {
            case "uid":
            case "usearch":
            case "email":
            case "collegeName":
              break;
            default:
              nc[k] = v;
              break;
          }
        });
        this.criteria = nc;
        this.loadSubmissions();
      }
    },
    syncSubUpdated(subId = '', key = '', boolVal = false, workId = '') {
      const subIndex = this.items.findIndex(sub => sub._id === subId);
      if (subIndex >= 0) {
        const item = Object.assign({}, this.items[subIndex]);
        if (item instanceof Object) {
          const keys = Object.keys(item);
          if (notEmptyString(workId, 16) && key === "selected") {
            const w = item.works.find(w => w.id == workId);
            if (w instanceof Object) {
              w.selected = boolVal;
            }
          } else if (keys.includes(key)) {
            const item = Object.assign({}, this.items[subIndex]);
            this.items.splice(subIndex, 1);
            if (key === 'signUp') {
              item.user.signUp = boolVal;
            }
            item[key] = boolVal;
            this.items.splice(subIndex, 0, item);
          }
        }
      }
    },
    close() {
      const parts = this.$route.path.substring(1).split("/");
      if (parts.length > 1) {
        const subId = parts.pop();
        if (notEmptyString(subId, 16) && /^[a-f0-9]+$/i.test(subId)) {
          this.$scrollTo(`#t-row-${subId}`, 250);
        }
        this.showDetail = parts.length > 1;
        const newPath = "/" + parts.join("/");
        if (this.$route.path !== newPath) {
          this.$router.push({
            path: newPath,
            query: this.$route.query
          });
        }
      }
    },
    setQueryString() {
      let params = {};
      if (this.primaryFilter.length > 0) {
        params.status = this.primaryFilter;
      }
      if (this.mediaFilter) {
        params.media = this.mediaFilter;
      }
      if (notEmptyString(this.search, 1)) {
        params.search = this.search;
        if (notEmptyString(this.searchMode, 1)) {
          params.searchMode = this.searchMode;
        }
      }
      if (notEmptyString(this.lastName, 0)) {
        params.lastName = this.lastName;
      }

      if (notEmptyString(this.sortOpt, 2)) {
        params.sort = this.sortOpt;
      }
      params.page = this.page;
      const currKeys = Object.keys(this.$route.query);
      const newKeys = Object.keys(params);
      let sameQuery = false;
      if (newKeys.length === currKeys.length) {
        if (newKeys.every(k => currKeys.indexOf(k) >= 0)) {
          sameQuery = Object.entries(this.$route.query).every(entry => {
            const [k, v] = entry;
            return v === params[k];
          });
        }
      }
      if (!sameQuery) {
        this.$router.push({ path: "/submissions", query: params });
      }
    },
    assignRowClasses(row, index) {
      let cls = [];
      if (index % 2 === 0) {
        cls.push("even");
      }
      if (row.submitted) {
        cls.push("submitted");
      }
      if (row.shortlisted) {
        cls.push("shortlisted");
      }
      if (row.checked) {
        cls.push("checked");
      }
      if (row.archived) {
        cls.push("archived");
      }
      if (row.numWorks > 0) {
        cls.push("has-works");
      } else {
        cls.push("no-works");
      }
      if (row.numMediaItems > 0) {
        cls.push("has-media-items");
      }
      return cls.join(" ");
    },
    updateStatuses() {
      const updateVals = this.items.map(sub => {
        return {
          id: sub._id,
          ineligible: sub.ineligible,
          checked: sub.checked,
          marked: sub.marked,
          shortlisted: sub.shortlisted,
          deadlineException: sub.deadlineException
        };
      });
      const { _id } = this.user;

      bulkEdit({
        items: updateVals,
        user: _id
      }).then(out => {
        if (out.result) {
          this.loadSubmissions();
        }
      });
    },
    setCriteria(name) {
      if (notEmptyString(name, 2)) {
        this.criteria = {};
        this.criteria["status"] = name;
      }
      switch (this.mediaFilter) {
        case "hasVideo":
        case "hasAudio":
        case "hasImages":
          this.criteria[this.mediaFilter] = 1;
          break;
        case "noVideo":
          this.criteria.hasVideo = 0;
          break;
      }
      if (this.lastName.length === 1 && this.lastName !== "-") {
        this.criteria.lastName = this.lastName;
      }
      this.loadSubmissions();
    },
    matchSubmission() {
      if (this.$route.path) {
        const parts = this.$route.path.substring(1).split("/");
        if (parts.length > 1) {
          const subId = parts[1];
          if (subId.length > 5) {
            this.showSubmission(subId, true);
          }
        }
      }
    },
    onPageChange(page) {
      this.page = page;
      this.loadSubmissions();
    },
    restoreFilters() {
      const storedSort = this.$ls.get("list_sort");
      if (notEmptyString(storedSort, 2)) {
        this.sortOpt = storedSort;
      }
      const storedFilter = this.$ls.get("list_filter");
      if (notEmptyString(storedFilter, 2)) {
        this.primaryFilter = storedFilter;
      }
    },
    clearSearch() {
      this.search = "";
      this.criteria = {};
      this.restoreFilters();
      this.loadSubmissions();
    },
    handleStatus(row, name) {
      const rowIndex = this.items.findIndex(item => item._id === row._id);
      if (rowIndex >= 0) {
        const sub = this.items[rowIndex];
        if (sub instanceof Object) {
          disableSubmissionStatuses(this.items[rowIndex], name);
          const edited = {
            _id: sub._id,
            user: sub.user._id
          };
          edited[name] = sub[name];
          saveSubmission(edited).then(data => {
            if (data.valid) {
              const { uid } = sub.user;
              this.toast(`Submission ${uid} updated`);
            }
          });
        }
      }
    },
    updateHint(sub) {
      const str = "created at: " + this.shortDatetime(sub.createdAt) + ", updated at: " + this.shortDatetime(sub.modifiedAt);
      return str;
    },
    updateItem(subId, workId, item, newWork) {
      let { subIndex, sub, workIndex, work } = extractSubAndWorkRefs(
        this.items,
        subId,
        workId
      );
      let invIndex = -1;
      if (workIndex < 0 && subIndex >= 0) {
        invIndex = sub.invalidWorks.findIndex(w => w._id);
        if (invIndex >= 0) {
          workIndex = sub.works.length;
          work = sub.invalidWorks[invIndex];
        }
      }
      if (workIndex < 0) {
        if (newWork instanceof Object) {
          newWork.mediaItems = [mapMediaItem(item)];
          workIndex = sub.works.length;
          sub.works.push(newWork);
          work = newWork;
        }
      }

      if (workIndex >= 0 && sub instanceof Object && work instanceof Object) {
        const mIndex = work.mediaItems.findIndex(mi => mi._id === item._id);
        const mapped = mapMediaItem(item);
        if (mIndex >= 0) {
          work.mediaItems.splice(mIndex, 1, mapped);
        } else {
          work.mediaItems.push(mapped);
          if (invIndex > 0) {
            sub.works.push(work);
            sub.invalidWorks.splice(invIndex, 1);
          } else {
            sub.works.splice(workIndex, 1, work);
          }
          this.items.splice(subIndex, 1, sub);
          setTimeout(() => {
            this.$forceUpdate();
          }, 50);
          setTimeout(() => {
            bus.$emit("media-item-updated", item);
          }, 100);
        }
      }
    },
    updateWork(subId, workId, editedWork) {
      const { subIndex, sub, workIndex, work } = extractSubAndWorkRefs(
        this.items,
        subId,
        workId
      );

      if (sub instanceof Object && work instanceof Object) {
        if (workIndex >= 0) {
          sub.works.splice(workIndex, 1, mapWork(editedWork));
        } else {
          sub.works.push(mapWork(editedWork));
        }
        sub.hasSelectedWork = sub.works.filter(w => w.selected).length > 0;
        this.items.splice(subIndex, 1, sub);
        setTimeout(() => {
          this.$forceUpdate();
        }, 50);
        setTimeout(() => {
          bus.$emit(
            "work-updated",
            mapWork(editedWork),
            "selected",
            editedWork.selected
          );
        }, 100);
      }
    },
    deleteWorkItem(subId, workId, itemId) {
      const { subIndex, sub, workIndex, work } = extractSubAndWorkRefs(
        this.items,
        subId,
        workId
      );
      if (sub instanceof Object && work instanceof Object) {
        bus.$emit("media-item-removed", itemId);
        removeMediaItem(subId, workId, itemId).then(() => {
          const mIndex = work.mediaItems.findIndex(mi => mi._id === itemId);
          if (mIndex >= 0) {
            work.mediaItems.splice(mIndex, 1);
          }
          if (work.mediaItems.length < 1) {
            sub.works.splice(workIndex, 1);
            removeWork(subId, workId).then(res => {
              if (res) {
                this.toast(`Work deleted.`, 2000);
              }
            });
          } else {
            sub.works[workIndex] = work;
            this.toast(`Media item deleted.`, 2000);
          }
          this.items.splice(subIndex, 1, sub);
        });
      }
    },
    toast(message = "", duration = 3000) {
      this.$buefy.toast.open({
        duration,
        message,
        position: "is-bottom",
        type: "is-success"
      });
    },
    deleteInvalidWorks(submission) {
      if (submission instanceof Object) {
        if (
          submission.works instanceof Array &&
          notEmptyString(submission._id)
        ) {
          if (submission.works) {
            const edited = {
              _id: submission._id,
              user: submission.user._id,
              works: submission.works
            };
            saveSubmission(edited).then(data => {
              if (data) {
                const subIndex = this.items.findIndex(
                  s => s._id === submission._id
                );
                if (subIndex >= 0) {
                  this.items[subIndex].invalidWorks = [];
                  this.toast(`Invalid works deleted.`, 1800);
                }
              }
            });
          }
        }
      }
    },
    reload() {
      this.loadSubmissions(true);
    }
  }
};
</script>
